import {
    enableProdMode,
    ErrorHandler,
    provideAppInitializer,
    inject,
    LOCALE_ID,
    forwardRef,
    provideZonelessChangeDetection,
    importProvidersFrom,
} from '@angular/core';
import { loadTranslations, HttpLoaderFactory } from './app/app.module';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import { GlobalErrorHandler } from './app/core/services/global-error-handler.service';
import { initConfig, AppConfig } from './app/app.config';
import {
    TranslateService,
    TranslateModule,
    TranslateLoader,
    MissingTranslationHandler,
    MissingTranslationHandlerParams,
    TranslateCompiler,
} from '@ngx-translate/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { WINDOW, windowFactory } from '@app/shared/services/window.token';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
    HttpClient,
} from '@angular/common/http';
import { ApiInterceptor } from './app/core/interceptors/api.interceptor';
import { AdminTestApiHttpInterceptor } from './app/admin/pages/admin-test-api/admin-test-api.interceptor';
import { ToastrModule } from 'ngx-toastr';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app/app-routing.module';
import { NgxSpinnerModule } from 'ngx-spinner';
import { CoreModule } from '@app/core/core.module';
import { MaterialModule } from '@app/shared/material/material.module';
import { SelfserviceMainModule } from './app/external/pages/selfservice/selfservice-main.module';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { provideApiConfiguration } from 'api/api-configuration';
import { provideApiConfiguration as provideApiV3Configuration } from 'api-v3/api-configuration';
import { provideApiConfiguration as provideAddonsApiConfiguration } from 'api/addons/api-configuration';
import { provideApiConfiguration as provideAddonsV3ApiConfiguration } from 'api/addons-v3/api-configuration';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { OAuthModule } from 'angular-oauth2-oidc';
import { AuthModule, StsConfigLoader } from 'angular-auth-oidc-client';
import { managementAuthFactory } from '@app/management/management-auth';
import { AppComponent } from './app/app.component';
import { Router } from '@angular/router';

class CustomMissingTranslationHandler implements MissingTranslationHandler {
    handle(params: MissingTranslationHandlerParams) {
        return 'Missing translation for key: ' + params.key;
    }
}

/**
 * Resolve the Sentry release. CI writes `assets/version.json` with the same
 * RELEASE value it uses to upload source maps (see `.gitlab-ci.yml`), so using
 * it here guarantees the event's release matches the uploaded artifacts and
 * stack traces get symbolicated. Falls back to the package version for local
 * builds where `version.json` is not present.
 */
async function resolveSentryRelease(): Promise<string> {
    try {
        const response = await fetch(`assets/version.json?v=${Date.now()}`);
        if (response.ok) {
            const version = await response.json();
            if (version?.release) {
                return version.release;
            }
        }
    } catch {
        // ignore and fall back to the build-time version below
    }
    return environment.version;
}

interface ResolvedSentryConfig {
    dsn: string;
    enabled: boolean;
    environment: string;
    tracesSampleRate: number;
}

/**
 * Resolve the Sentry settings from the runtime `assets/config.json`, whose
 * `${SENTRY_*}` placeholders are substituted per environment at container
 * startup (same mechanism as `${API_URL}`). Sentry must be initialized before
 * Angular bootstraps, so we fetch the config here rather than relying on the
 * `AppConfig` APP_INITIALIZER, which runs after bootstrap. Falls back to the
 * build-time `environment.sentry` when a placeholder is un-substituted (local
 * builds) or the fetch fails.
 */
async function resolveSentryConfig(): Promise<ResolvedSentryConfig> {
    const fb = environment.sentry;
    let cfg: Record<string, string> = {};
    try {
        const res = await fetch(`assets/config.json?v=${Date.now()}`);
        if (res.ok) {
            cfg = await res.json();
        }
    } catch {
        // ignore and fall back to the build-time environment below
    }

    const pick = (val: string | undefined, placeholder: string) =>
        val && val !== placeholder ? val : undefined;

    return {
        dsn: pick(cfg['sentryDsn'], '${SENTRY_DSN}') ?? fb?.dsn ?? '',
        enabled:
            pick(cfg['sentryEnabled'], '${SENTRY_ENABLED}') !== undefined
                ? cfg['sentryEnabled'] === 'true'
                : (fb?.enabled ?? false),
        environment:
            pick(cfg['sentryEnvironment'], '${SENTRY_ENVIRONMENT}') ??
            fb?.environment ??
            'unknown',
        tracesSampleRate:
            pick(
                cfg['sentryTracesSampleRate'],
                '${SENTRY_TRACES_SAMPLE_RATE}'
            ) !== undefined
                ? parseFloat(cfg['sentryTracesSampleRate'])
                : (fb?.tracesSampleRate ?? 0),
    };
}

async function bootstrap(): Promise<void> {
    const sentryConfig = await resolveSentryConfig();
    if (sentryConfig.enabled && sentryConfig.dsn) {
        Sentry.init({
            dsn: sentryConfig.dsn,
            // Same-origin tunnel: the browser POSTs event envelopes to /telemetry
            // (served by our own nginx) instead of directly to Sentry, so
            // ad-blockers -- which match the sentry_key param and /envelope/ path --
            // never see them. nginx relays them server-side. Relative URL keeps it
            // same-origin (no CORS preflight); works because this is a static SPA.
            tunnel: '/telemetry',
            environment: sentryConfig.environment,
            release: await resolveSentryRelease(),
            integrations: [Sentry.browserTracingIntegration()],
            tracesSampleRate: sentryConfig.tracesSampleRate,
            beforeSend(event) {
                const message = event.exception?.values?.[0]?.value || '';
                if (/Loading chunk [\d]+ failed/.test(message)) {
                    return null;
                }
                return event;
            },
        });
    }

    if (environment.production) {
        enableProdMode();
        if (window) {
            window.console.log = () => {};
            window.console.warn = () => {};
            window.console.info = () => {};
        }
    }

    await bootstrapApplication(AppComponent, {
        providers: [
            importProvidersFrom(
                ToastrModule.forRoot({
                    timeOut: 10000,
                    positionClass: 'toast-top-right',
                }),
                ReactiveFormsModule,
                FormlyModule.forRoot(),
                FormlyBootstrapModule,
                BrowserModule,
                BrowserAnimationsModule,
                AppRoutingModule,
                NgxSpinnerModule,
                CoreModule,
                MaterialModule,
                SelfserviceMainModule,
                StoreModule.forRoot({}, {}),
                environment.production ? [] : StoreDevtoolsModule.instrument(),
                TranslateModule.forRoot({
                    defaultLanguage: 'de',
                    loader: {
                        provide: TranslateLoader,
                        useFactory: HttpLoaderFactory,
                        deps: [HttpClient],
                    },
                    missingTranslationHandler: {
                        provide: MissingTranslationHandler,
                        useClass: CustomMissingTranslationHandler,
                    },
                    compiler: {
                        provide: TranslateCompiler,
                        useClass: TranslateMessageFormatCompiler,
                    },
                }),
                OAuthModule.forRoot(),
                AuthModule.forRoot({
                    loader: {
                        provide: StsConfigLoader,
                        useFactory: managementAuthFactory,
                        deps: [AppConfig],
                    },
                }),
            ),
            // API root URLs. The generated clients no longer ship an NgModule
            // (ng-openapi-gen 1.0+), each exposes a provider function instead.
            // The literal values are rewritten per environment by ApiInterceptor.
            provideApiConfiguration('apiURL'),
            provideApiV3Configuration('apiV3Url'),
            provideAddonsApiConfiguration('addonsURL'),
            provideAddonsV3ApiConfiguration('addonsV3Url'),
            // Error handling
            { provide: ErrorHandler, useClass: GlobalErrorHandler },
            // Sentry performance tracing
            {
                provide: Sentry.TraceService,
                deps: [Router],
            },
            provideAppInitializer(() => {
                inject(Sentry.TraceService);
            }),
            // App initializers
            provideAppInitializer(() => {
                const initializerFn = initConfig(inject(AppConfig));
                return initializerFn();
            }),
            provideAppInitializer(() => {
                const initializerFn = loadTranslations(
                    inject(TranslateService)
                );
                return initializerFn();
            }),
            // Other providers
            { provide: LOCALE_ID, useValue: 'de' },
            {
                provide: MAT_DIALOG_DEFAULT_OPTIONS,
                useValue: { hasBackdrop: true, disableClose: false },
            },
            {
                provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
                useValue: { subscriptSizing: 'dynamic' },
            },
            { provide: WINDOW, useFactory: windowFactory },
            // HTTP Interceptors
            {
                provide: HTTP_INTERCEPTORS,
                useExisting: forwardRef(() => ApiInterceptor),
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AdminTestApiHttpInterceptor,
                multi: true,
            },
            provideHttpClient(withInterceptorsFromDi()),
            // Zoneless change detection
            provideZonelessChangeDetection(),
        ],
    });
}

bootstrap().catch(err => console.error(err));
